import axios from 'axios';
import { toast } from 'sonner';
import { t } from 'i18next';
import { API_URL } from '../config';
import { XIMI_SSO_CALLBACK_PATH } from '../routePaths';
import type { AuthTokens } from '../models/auth';
import {
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    clearTokens
} from './storage/tokenStorage';
import {
    XIMI_SSO_HEADER,
    ensureFreshXimiSsoAccessToken,
    clearXimiSsoSession
} from './ximi/ximiSsoSession';

const api = axios.create({ baseURL: API_URL });

/** Codes 401 signalant une preuve SSO Ximi manquante/invalide (≠ access token Stimul'In). */
const SSO_PROOF_ERROR_CODES = ['SSO_REQUIRED', 'SSO_INVALID', 'SSO_MISMATCH'];

/** 401 dont le code désigne la preuve SSO Ximi (et non l'access token Stimul'In). */
const isXimiSsoProofError = (error: unknown): boolean =>
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    SSO_PROOF_ERROR_CODES.includes(error.response?.data?.code);

/** Un seul toast « trop de requêtes » par fenêtre — évite le spam si plusieurs appels tombent en 429. */
const RATE_LIMIT_TOAST_THROTTLE_MS = 10_000;
let lastRateLimitToastAt = 0;

const notifyRateLimited = (): void => {
    const now = Date.now();
    if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_THROTTLE_MS) return;
    lastRateLimitToastAt = now;
    toast.warning(t('Trop de requêtes. Veuillez patienter un instant.'));
};

let isRefreshing = false;
let pendingQueue: any[] = [];

const processQueue = (error: unknown, token: string | null) => {
    pendingQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token!);
        }
    });
    pendingQueue = [];
};

/** Session SSO Ximi définitivement morte : purge locale + retour login (bandeau ximiError). */
const handleXimiSsoSessionLost = async (error: unknown): Promise<never> => {
    await clearTokens();
    await clearXimiSsoSession();
    const { pathname } = window.location;
    if (!pathname.startsWith('/login') && pathname !== XIMI_SSO_CALLBACK_PATH) {
        window.location.href = '/login?ximiError=1';
    }
    return Promise.reject(error);
};

api.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Preuve SSO par requête (users Ximi) — null pour les users non-Ximi (aucune session oidc).
    const ssoToken = await ensureFreshXimiSsoAccessToken();
    if (ssoToken) {
        config.headers[XIMI_SSO_HEADER] = ssoToken;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Network error or no config (no response from server) — nothing to retry
        if (!originalRequest) {
            return Promise.reject(error);
        }

        // 429 rate-limit API : informer sans toucher à la session, pas de retry automatique
        if (error.response?.status === 429) {
            notifyRateLimited();
            return Promise.reject(error);
        }

        // Preuve SSO rejetée : le token Stimul'In est bon, c'est la session SSO qui cloche.
        // Un renew + retry, sinon session perdue → logout.
        if (isXimiSsoProofError(error)) {
            if (originalRequest._ssoRetry) return handleXimiSsoSessionLost(error);
            originalRequest._ssoRetry = true;

            const ssoToken = await ensureFreshXimiSsoAccessToken();
            if (!ssoToken) return handleXimiSsoSessionLost(error);
            originalRequest.headers[XIMI_SSO_HEADER] = ssoToken;
            return api(originalRequest);
        }

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
            isRefreshing = false;
            processQueue(error, null);
            return Promise.reject(error);
        }

        try {
            // La rotation d'un user Ximi exige aussi la preuve SSO.
            const ssoToken = await ensureFreshXimiSsoAccessToken();
            const { data } = await axios.post<AuthTokens>(
                `${API_URL}/auth/refresh`,
                { refreshToken },
                { headers: ssoToken ? { [XIMI_SSO_HEADER]: ssoToken } : {} }
            );
            await setAccessToken(data.accessToken);
            await setRefreshToken(data.refreshToken);
            processQueue(null, data.accessToken);
            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
            return await api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            if (isXimiSsoProofError(refreshError)) {
                return await handleXimiSsoSessionLost(refreshError);
            }
            await clearTokens();
            // Avoid redirect loop if already on login page, and don't interrupt
            // the Ximi SSO callback (it has its own auth flow in progress).
            const { pathname } = window.location;
            if (!pathname.startsWith('/login') && pathname !== XIMI_SSO_CALLBACK_PATH) {
                window.location.href = '/login';
            }
            return await Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
